import { NavContext } from '@ionic/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { useClinic } from './ClinicContext';
import { useDentist } from '@/contexts/DentistContext';
import { useTracker } from '@/contexts/TrackerContext';
import { DentistHour, createDentistHour } from '@/data/DentistHours';
import { Dentist, createDentist, deleteDentist } from '@/data/Dentists';
import { Service, createService } from '@/data/Services';
import { useToast } from '@/hooks';
import { ADD_DENTIST_STEPS } from '@/utils/constants';
import { ROUTES } from '@/utils/routes';
import { EVENTS } from '@/utils/tracker';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface AddDentistContextProps {
  loading: boolean;
  currentStep: string;
  setCurrentStep: React.Dispatch<React.SetStateAction<string>>;
  goBack: () => void;
  firstName: string | null;
  setFirstName: React.Dispatch<React.SetStateAction<string | null>>;
  lastName: string | null;
  setLastName: React.Dispatch<React.SetStateAction<string | null>>;
  onAddDentist: (hours: DentistHour[]) => Promise<void>;
  onReset: () => Promise<void>;
  services: Service[];
  setServices: React.Dispatch<React.SetStateAction<Service[]>>;
  onCreateServices: (services: Service[]) => Promise<void>;
  onSkip: () => void;
}

const AddDentistContext = createContext<AddDentistContextProps | undefined>(
  undefined
);

interface AddDentistProviderProps {
  children: React.ReactNode;
}

export const AddDentistProvider: React.FC<AddDentistProviderProps> = ({
  children
}) => {
  const { navigate, goBack } = useContext(NavContext);
  const { selectedClinic: clinic } = useClinic();
  const { dentists, selectDentist, setDentists } = useDentist();
  const { captureEvent } = useTracker();
  const displayToast = useToast();
  const queryClient = useQueryClient();

  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<string>(
    ADD_DENTIST_STEPS.NAME
  );
  const [dentist, setDentist] = useState<Dentist | null>(null);
  const [services, setServices] = useState<Service[]>([]);

  const createDentistMutation = useMutation({
    mutationFn: async () => {
      if (
        !(firstName && lastName && firstName.length > 1 && lastName.length > 1)
      ) {
        throw new Error('Please enter a valid first and last name.');
      }

      if (!clinic?.id) {
        throw new Error('Invalid clinic ID. Please contact support.');
      }

      const sanitizedFirstName = firstName?.replace(/^(dr\.|Dr\.)\s*/i, '');
      return await queryClient.fetchQuery({
        queryKey: ['createDentist', clinic?.id, sanitizedFirstName, lastName],
        queryFn: () =>
          createDentist({
            first_name: sanitizedFirstName,
            last_name: lastName,
            clinic_id: clinic?.id!
          } satisfies Dentist)
      });
    },
    onSuccess: (newDentist) => {
      if (newDentist) {
        setDentists([...dentists, newDentist]);
      }
    },
    onError: (error) => {
      displayToast({
        message:
          error instanceof Error
            ? error.message
            : 'Error creating dentist. Please contact support.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  });

  const createDentistHoursMutation = useMutation({
    mutationFn: async ({
      hours,
      dentistId
    }: {
      hours: DentistHour[];
      dentistId: string;
    }) => {
      const defaultHour = {
        day: 0,
        start_time: null,
        end_time: null,
        open: false
      };

      const completeHours = Array.from({ length: 7 }, (_, index) => ({
        ...defaultHour,
        day: index,
        ...hours.find((h) => h.day === index)
      }));

      return await Promise.all(
        completeHours.map(async (hour) => {
          const dentistHour = await queryClient.fetchQuery({
            queryKey: ['createDentistHour', dentistId, hour.day],
            queryFn: () =>
              createDentistHour({
                ...hour,
                dentist_id: dentistId
              } satisfies DentistHour)
          });

          if (!dentistHour) {
            throw new Error(`Failed to create hours for day ${hour.day}`);
          }

          return dentistHour;
        })
      );
    },
    onError: () => {
      displayToast({
        message: 'Failed to set working hours. Please try again.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  });

  const createServicesMutation = useMutation({
    mutationFn: async (services: Service[]) => {
      if (!dentist?.id) {
        throw new Error('Dentist is not set. Please contact support.');
      }

      const dentistServices = await Promise.all(
        services.map((service) =>
          createService({
            name: service.name,
            duration: service.duration,
            dentist_id: dentist.id
          })
        )
      );

      const hasErrors = dentistServices.some((service) => service === null);
      if (hasErrors) {
        throw new Error('Error creating dentist services.');
      }

      return dentistServices;
    },
    onSuccess: () => {
      displayToast({
        message: 'Dentist added successfully! Welcome to your dashboard.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
      selectDentist(dentist);
      navigate(ROUTES.CALENDAR);
    },
    onError: (error) => {
      displayToast({
        message:
          error instanceof Error
            ? error.message
            : 'Error creating services. Please add them later.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
      onSkip();
    }
  });

  const onReset = useCallback(async () => {
    selectDentist(null);
    setCurrentStep(ADD_DENTIST_STEPS.NAME);
  }, [selectDentist]);

  const onAddDentist = useCallback(
    async (hours: DentistHour[]) => {
      try {
        const newDentist = await createDentistMutation.mutateAsync();
        if (newDentist?.id) {
          const dentistHours = await createDentistHoursMutation.mutateAsync({
            hours,
            dentistId: newDentist.id
          });
          if (dentistHours) {
            setDentist(newDentist);
            captureEvent({
              event: EVENTS.DENTIST_ADDED,
              properties: { dentist_id: newDentist.id }
            });
            setCurrentStep(ADD_DENTIST_STEPS.SERVICES);
          } else {
            await queryClient.fetchQuery({
              queryKey: ['deleteDentist', newDentist.id],
              queryFn: () => deleteDentist(newDentist.id!)
            });
            onReset();
          }
        } else {
          onReset();
        }
      } catch {
        onReset();
      }
    },
    [createDentistMutation, createDentistHoursMutation, captureEvent, onReset]
  );

  const onSkip = useCallback(() => {
    displayToast({
      message:
        'Skipped adding services, you can add them later. Welcome to your dashboard!',
      duration: 3000,
      position: 'bottom',
      positionAnchor: 'tab-bar'
    });
    selectDentist(dentist);
    navigate(ROUTES.CALENDAR);
  }, [dentist, navigate, selectDentist]);

  const onCreateServices = useCallback(
    async (services: Service[]) => {
      await createServicesMutation.mutateAsync(services);
    },
    [createServicesMutation]
  );

  useEffect(() => {
    return () => {
      setFirstName(null);
      setLastName(null);
      setCurrentStep(ADD_DENTIST_STEPS.NAME);
      setDentist(null);
      setServices([]);
    };
  }, []);

  const loading =
    createDentistMutation.isPending ||
    createDentistHoursMutation.isPending ||
    createServicesMutation.isPending;

  return (
    <AddDentistContext.Provider
      value={{
        loading,
        currentStep,
        setCurrentStep,
        goBack,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        onAddDentist,
        onReset,
        services,
        setServices,
        onCreateServices,
        onSkip
      }}
    >
      {children}
    </AddDentistContext.Provider>
  );
};

export const useAddDentist = () => {
  const context = useContext(AddDentistContext);
  if (context === undefined) {
    throw new Error('useAddDentist must be used within a AddDentistProvider');
  }
  return context;
};
